import { supabase } from '../lib/supabase';

export interface ChatMessage {
  message_id: string;
  role: 'user' | 'assistant';
  content: string;
  sent_to_user?: string;
  created_at: string;
  updated_at?: string;
  chat_thread_id: string;
  session_title?: string;
  session_created_at?: string;
  session_updated_at?: string;
  client_id?: string;
  user_id?: string;
}

export interface SessionContextMeta {
  context_type: string;
  context_params: Record<string, string>;
}

export interface CreateMessageInput {
  role: 'user' | 'assistant';
  content: string;
  sent_to_user?: string;
  chat_thread_id: string;
  session_title?: string;
  context_type?: string;
  context_params?: Record<string, string>;
}

export interface ChatSession {
  chat_thread_id: string;
  session_title: string;
  session_created_at: string;
  session_updated_at: string;
  message_count?: number;
  first_message?: string;
}

function getCurrentUserIdentity(): { user_id: string; client_id: string } | null {
  try {
    const saved = localStorage.getItem('user');
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (parsed.email && parsed.client_id) {
      return { user_id: parsed.email, client_id: parsed.client_id };
    }
    return null;
  } catch {
    return null;
  }
}

function buildSessionsFromRows(data: any[]): ChatSession[] {
  const sessionMap = new Map<string, ChatSession>();

  data?.forEach((row) => {
    const threadId = row.chat_thread_id;
    if (!threadId) return;

    if (!sessionMap.has(threadId)) {
      sessionMap.set(threadId, {
        chat_thread_id: threadId,
        session_title: row.session_title || '',
        session_created_at: row.session_created_at,
        session_updated_at: row.session_updated_at,
        message_count: 1,
        first_message: row.role === 'user' ? row.content : undefined,
      });
    } else {
      const session = sessionMap.get(threadId)!;
      session.message_count = (session.message_count || 0) + 1;
      if (!session.session_title && row.session_title) {
        session.session_title = row.session_title;
      }
      if (!session.first_message && row.role === 'user') {
        session.first_message = row.content;
      }
    }
  });

  const sessions = Array.from(sessionMap.values());
  for (const session of sessions) {
    if (!session.session_title) {
      session.session_title = session.first_message
        ? session.first_message.substring(0, 50)
        : 'New Chat';
    }
  }

  return sessions;
}

export const salesCoachService = {
  async getAllSessions(): Promise<ChatSession[]> {
    const identity = getCurrentUserIdentity();
    let query = supabase
      .from('sales_coach_chat')
      .select('chat_thread_id, session_title, session_created_at, session_updated_at, content, role')
      .not('chat_thread_id', 'is', null)
      .order('session_updated_at', { ascending: false });

    if (identity) {
      query = query.eq('user_id', identity.user_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }

    return buildSessionsFromRows(data || []);
  },

  async getSessionMessages(chatThreadId: string): Promise<ChatMessage[]> {
    const identity = getCurrentUserIdentity();
    let query = supabase
      .from('sales_coach_chat')
      .select('*')
      .eq('chat_thread_id', chatThreadId)
      .order('created_at', { ascending: true });

    if (identity) {
      query = query.eq('user_id', identity.user_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching session messages:', error);
      throw error;
    }

    return data as ChatMessage[];
  },

  async createMessage(message: CreateMessageInput): Promise<ChatMessage> {
    const now = new Date().toISOString();
    const identity = getCurrentUserIdentity();

    const insertPayload: Record<string, any> = {
      role: message.role,
      content: message.content,
      sent_to_user: message.sent_to_user || null,
      chat_thread_id: message.chat_thread_id,
      session_created_at: now,
      session_updated_at: now,
    };

    if (message.session_title) {
      insertPayload.session_title = message.session_title;
    }

    if (identity) {
      insertPayload.user_id = identity.user_id;
      insertPayload.client_id = identity.client_id;
    }

    if (message.context_type) {
      insertPayload.context_type = message.context_type;
    }
    if (message.context_params) {
      insertPayload.context_params = message.context_params;
    }

    const { data, error } = await supabase
      .from('sales_coach_chat')
      .insert([insertPayload])
      .select()
      .single();

    if (error) {
      console.error('Error creating message:', error);
      throw error;
    }

    await this.updateSessionTimestamp(message.chat_thread_id);

    return data as ChatMessage;
  },

  async updateSessionTimestamp(chatThreadId: string): Promise<void> {
    const now = new Date().toISOString();

    const { error } = await supabase
      .from('sales_coach_chat')
      .update({ session_updated_at: now })
      .eq('chat_thread_id', chatThreadId);

    if (error) {
      console.error('Error updating session timestamp:', error);
    }
  },

  async updateSessionTitle(chatThreadId: string, newTitle: string): Promise<void> {
    const { error } = await supabase
      .from('sales_coach_chat')
      .update({ session_title: newTitle })
      .eq('chat_thread_id', chatThreadId);

    if (error) {
      console.error('Error updating session title:', error);
      throw error;
    }
  },

  async deleteSession(chatThreadId: string): Promise<void> {
    const { error } = await supabase
      .from('sales_coach_chat')
      .delete()
      .eq('chat_thread_id', chatThreadId);

    if (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  },

  async deleteAllMessages(): Promise<void> {
    const { error } = await supabase
      .from('sales_coach_chat')
      .delete()
      .not('message_id', 'is', null);

    if (error) {
      console.error('Error deleting messages:', error);
      throw error;
    }
  },

  async searchSessions(query: string): Promise<ChatSession[]> {
    const identity = getCurrentUserIdentity();
    let dbQuery = supabase
      .from('sales_coach_chat')
      .select('chat_thread_id, session_title, session_created_at, session_updated_at, content, role')
      .not('chat_thread_id', 'is', null)
      .or(`session_title.ilike.%${query}%,content.ilike.%${query}%`)
      .order('session_updated_at', { ascending: false });

    if (identity) {
      dbQuery = dbQuery.eq('user_id', identity.user_id);
    }

    const { data, error } = await dbQuery;

    if (error) {
      console.error('Error searching sessions:', error);
      throw error;
    }

    return buildSessionsFromRows(data || []);
  },

  async getSessionContextMeta(chatThreadId: string): Promise<SessionContextMeta | null> {
    const identity = getCurrentUserIdentity();
    let query = supabase
      .from('sales_coach_chat')
      .select('context_type, context_params')
      .eq('chat_thread_id', chatThreadId)
      .not('context_type', 'is', null)
      .order('created_at', { ascending: true })
      .limit(1);

    if (identity) {
      query = query.eq('user_id', identity.user_id);
    }

    const { data, error } = await query.maybeSingle();

    if (error || !data?.context_type) return null;

    return {
      context_type: data.context_type,
      context_params: data.context_params || {},
    };
  },

  subscribeToMessages(chatThreadId: string | null, callback: (message: ChatMessage) => void) {
    const identity = getCurrentUserIdentity();
    const channelName = `sales_coach_chat_${chatThreadId || 'global'}_${Date.now()}`;

    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sales_coach_chat',
        },
        (payload) => {
          const message = payload.new as ChatMessage;
          if (identity && message.user_id && message.user_id !== identity.user_id) return;
          if (!chatThreadId || message.chat_thread_id === chatThreadId) {
            callback(message);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  },
};
